import React, { useEffect, useState } from "react";
import {
  Container, Typography, Box, Paper, TextField, Button,
  Grid, MenuItem, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, IconButton, Divider
} from "@mui/material";
import {
  collection, doc, getDoc, getDocs, setDoc,
  updateDoc, increment, Timestamp, arrayUnion
} from "firebase/firestore";
import { db } from "./firebase";
import { useNavigate } from "react-router-dom";

const SIZES = [
  "30x40", "40x60", "50x70", "60x80", "70x90", "70x100", "80x120",
  "120x180", "160x240", "30x30", "40x40", "50x50", "60x60", "70x70",
  "80x80", "90x90", "100x100", "110x110", "120x120", "130x130",
];

const BaneenInventory = () => {
  const navigate = useNavigate();
  const [inventory, setInventory] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCity, setFilterCity] = useState("all"); 

  const [city, setCity] = useState("");
  const [category, setCategory] = useState("");
  const [material, setMaterial] = useState("");
  const [withFrame, setWithFrame] = useState("");
  const [frameType, setFrameType] = useState("");
  const [size, setSize] = useState("");
  const [qty, setQty] = useState("");
  const [price, setPrice] = useState("");

  const [customWidth, setCustomWidth] = useState("");
  const [customHeight, setCustomHeight] = useState("");
  const [frameLength, setFrameLength] = useState("");
  const [fullFrameKind, setFullFrameKind] = useState("");
  const [frameColor, setFrameColor] = useState("");

  const rawUser = localStorage.getItem("username");
  const isAdmin = rawUser === "admin";

  useEffect(() => { fetchInventory(); }, []);

  const fetchInventory = async () => {
    const snap = await getDocs(collection(db, "inventory"));
    const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    const sortedData = data.sort((a, b) => (b.updatedAt?.seconds || 0) - (a.updatedAt?.seconds || 0));
    setInventory(sortedData);
  };

  const resetFields = () => {
    setQty(""); setCategory(""); setMaterial(""); setWithFrame("");
    setFrameType(""); setSize(""); setCustomWidth(""); setCustomHeight("");
    setFrameLength(""); setFullFrameKind(""); setFrameColor(""); setPrice("");
  };

  const handleSaveNew = async () => {
    if (!city || !category || !qty) return alert("⚠️ أكمل الحقول الأساسية");
    const note = window.prompt("📝 يرجى كتابة سبب الإضافة (إجباري):");
    if (!note) return alert("❌ لا يمكن الإضافة بدون ذكر السبب");

    const enteredQty = Number(qty);
    let finalSize = size === "custom" ? `${customWidth}x${customHeight}` : size;
    let finalId = `${city}_${category}_${material}_${finalSize}_${fullFrameKind}_${frameColor}_${frameType}`;

    const ref = doc(db, "inventory", finalId);
    let logEntry = { type: "إضافة جديد", quantity: enteredQty, date: new Date().toLocaleString("en-US"), reason: note };

    let dataToSave = {
      city, category, material, withFrame, frameType, size: finalSize,
      fullFrameKind, frameLength, frameColor,
      quantity: increment(enteredQty), updatedAt: Timestamp.now(), logs: arrayUnion(logEntry)
    };
    if (isAdmin && price) dataToSave.price = Number(price);

    await setDoc(ref, dataToSave, { merge: true });
    alert("✅ تم الحفظ");
    resetFields();
    fetchInventory();
  };

  const handleQuickUpdate = async (item, type) => {
    const val = window.prompt(type === "add" ? "Enter Qty to Add:" : "Enter Qty to Remove:", "1");
    if (!val || isNaN(val) || Number(val) <= 0) return;
    const note = window.prompt("📝 Reason (Mandatory):");
    if (!note) return alert("❌ Reason required!");

    const change = Number(val);
    if (type === "remove" && change > item.quantity) return alert("❌ Insufficient qty");

    const ref = doc(db, "inventory", item.id);
    await updateDoc(ref, {
      quantity: increment(type === "add" ? change : -change),
      updatedAt: Timestamp.now(),
      logs: arrayUnion({ type: type === "add" ? "Add" : "Remove", quantity: change, date: new Date().toLocaleString("en-US"), reason: note })
    });
    fetchInventory();
  };

  const calculateTotal = (cityName) => {
    return inventory
      .filter(i => cityName === "all" ? true : i.city === cityName)
      .reduce((sum, i) => sum + (Number(i.quantity || 0) * Number(i.price || 0)), 0);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }} dir="rtl">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight={900} color="primary">📦 إدارة المخزن والمالية</Typography>
        <Button variant="outlined" size="small" onClick={() => navigate("/baneen")}>رجوع ⬅</Button>
      </Box>

      {isAdmin && (
        <Grid container spacing={2} mb={4}>
          {["all", "baghdad", "erbil"].map((loc) => (
            <Grid item xs={loc === "all" ? 12 : 6} md={4} key={loc}>
              <Paper 
                onClick={() => setFilterCity(loc)}
                sx={{ p: 2, textAlign: 'center', cursor: 'pointer', transition: '0.3s', bgcolor: filterCity === loc ? '#e3f2fd' : 'white', border: filterCity === loc ? '2px solid #1976d2' : '1px solid #ddd', '&:hover': { boxShadow: 4 } }}
              >
                <Typography variant="caption" fontWeight="bold" color="textSecondary">
                  {loc === "all" ? "المجموع الكلي" : loc === "baghdad" ? "مجموع بغداد" : "مجموع أربيل"}
                </Typography>
                <Typography variant="h6" fontWeight={900} color="primary">{calculateTotal(loc).toLocaleString()} IQD</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 3, borderRadius: 4, borderTop: '6px solid #1976d2' }}>
            <Typography variant="subtitle1" fontWeight={700} mb={2}>تسجيل صنف جديد</Typography>
            <TextField select fullWidth label="المدينة" value={city} onChange={(e) => { setCity(e.target.value); resetFields(); }} sx={{ mb: 2 }}>
              <MenuItem value="baghdad">بغداد</MenuItem>
              <MenuItem value="erbil">أربيل</MenuItem>
            </TextField>

            {city && (
              <TextField select fullWidth label="نوع المادة" value={category} onChange={(e) => setCategory(e.target.value)} sx={{ mb: 2 }}>
                <MenuItem value="لوحة">لوحة</MenuItem>
                <MenuItem value="لوحة جام">لوحة جام</MenuItem>
                <MenuItem value="إطار كامل">إطار كامل</MenuItem>
                <MenuItem value="إطار خشب مفصل">إطار خشب مفصل</MenuItem>
                <MenuItem value="إطار ال مفصل">إطار ال مفصل</MenuItem>
                <MenuItem value="إطار جام مفصل">إطار جام مفصل</MenuItem>
              </TextField>
            )}

            {category === "لوحة" && (
              <><TextField select fullWidth label="النوع" value={material} onChange={(e) => setMaterial(e.target.value)} sx={{ mb: 2 }}><MenuItem value="قماش">قماش</MenuItem><MenuItem value="خشب">خشب</MenuItem></TextField>
                {material && <TextField select fullWidth label="إطار" value={withFrame} onChange={(e) => setWithFrame(e.target.value)} sx={{ mb: 2 }}><MenuItem value="مع">مع إطار</MenuItem><MenuItem value="بدون">بدون إطار</MenuItem></TextField>}
                {withFrame === "مع" && <TextField select fullWidth label="نوع الإطار" value={frameType} onChange={(e) => setFrameType(e.target.value)} sx={{ mb: 2 }}><MenuItem value="L">L</MenuItem><MenuItem value="كاس مفرد">كاس مفرد</MenuItem><MenuItem value="كاس دبل">كاس دبل</MenuItem></TextField>}
                {(withFrame === "بدون" || frameType) && <TextField select fullWidth label="القياس" value={size} onChange={(e) => setSize(e.target.value)} sx={{ mb: 2 }}>{SIZES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}<MenuItem value="custom">أخرى</MenuItem></TextField>}
              </>
            )}

            {category === "لوحة جام" && <TextField select fullWidth label="القياس" value={size} onChange={(e) => setSize(e.target.value)} sx={{ mb: 2 }}><MenuItem value="40x30">40 * 30 سم</MenuItem></TextField>}
            {category === "إطار جام مفصل" && <TextField select fullWidth label="القياس" value={size} onChange={(e) => setSize(e.target.value)} sx={{ mb: 2 }}><MenuItem value="57x46.5">57 * 46.5 سم</MenuItem></TextField>}
            {category === "إطار كامل" && (
              <><TextField fullWidth label="الطول (م)" type="number" value={frameLength} onChange={(e) => setFrameLength(e.target.value)} sx={{ mb: 2 }} />
                <TextField select fullWidth label="النوع" value={fullFrameKind} onChange={(e) => setFullFrameKind(e.target.value)} sx={{ mb: 2 }}><MenuItem value="L">L</MenuItem><MenuItem value="كاس مفرد">كاس مفرد</MenuItem><MenuItem value="كاس دبل">كاس دبل</MenuItem></TextField>
                <TextField select fullWidth label="اللون" value={frameColor} onChange={(e) => setFrameColor(e.target.value)} sx={{ mb: 2 }}><MenuItem value="أسود">أسود</MenuItem><MenuItem value="ذهبي">ذهبي</MenuItem><MenuItem value="شامبين">شامبين</MenuItem><MenuItem value="أبيض">أبيض</MenuItem></TextField>
              </>
            )}
            {(category.includes("مفصل") && category !== "إطار جام مفصل") && <TextField select fullWidth label="القياس" value={size} onChange={(e) => setSize(e.target.value)} sx={{ mb: 2 }}>{SIZES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}<MenuItem value="custom">أخرى</MenuItem></TextField>}

            {size === "custom" && <Box display="flex" gap={1} mb={2}><TextField fullWidth label="طول" type="number" value={customWidth} onChange={(e) => setCustomWidth(e.target.value)} /><TextField fullWidth label="عرض" type="number" value={customHeight} onChange={(e) => setCustomHeight(e.target.value)} /></Box>}

            {category && (
              <><TextField fullWidth type="number" label="الكمية / العدد" value={qty} onChange={(e) => setQty(e.target.value)} sx={{ mb: 2 }} />
                {isAdmin && <TextField fullWidth label="السعر" type="number" value={price} onChange={(e) => setPrice(e.target.value)} sx={{ mb: 3 }} />}
                <Button fullWidth variant="contained" size="large" onClick={handleSaveNew} sx={{ borderRadius: 3, fontWeight: 'bold', py: 1.5 }}>💾 إضافة للمخزن</Button>
              </>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 4, border: '1px solid #eee', bgcolor: '#fafafa' }}>
            <Box display="flex" justifyContent="space-between" mb={2} alignItems="center">
              <Typography variant="h6" fontWeight={800}>📊 حالة المخزن</Typography>
              <TextField size="small" placeholder="بحث..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} sx={{ width: 250, bgcolor: 'white' }} />
            </Box>

            <TableContainer sx={{ maxHeight: 650, borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell align="right" sx={{ bgcolor: '#1976d2', color: 'white', fontWeight: 'bold', width: '100px' }}>المدينة</TableCell>
                    <TableCell align="right" sx={{ bgcolor: '#1976d2', color: 'white', fontWeight: 'bold', width: '120px' }}>الفئة</TableCell>
                    <TableCell align="right" sx={{ bgcolor: '#1976d2', color: 'white', fontWeight: 'bold' }}>التفاصيل والقياس</TableCell>
                    <TableCell align="center" sx={{ bgcolor: '#1976d2', color: 'white', fontWeight: 'bold', width: '130px' }}>الكمية</TableCell>
                    {isAdmin && <TableCell align="right" sx={{ bgcolor: '#1976d2', color: 'white', fontWeight: 'bold', width: '100px' }}>السعر</TableCell>}
                    {isAdmin && <TableCell align="right" sx={{ bgcolor: '#1976d2', color: 'white', fontWeight: 'bold', width: '110px' }}>المجموع</TableCell>}
                    <TableCell align="right" sx={{ bgcolor: '#1976d2', color: 'white', fontWeight: 'bold', width: '100px' }}>الحركة</TableCell>
                    {isAdmin && <TableCell align="center" sx={{ bgcolor: '#1976d2', color: 'white', fontWeight: 'bold', width: '50px' }}>📜</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {inventory
                    .filter(i => (filterCity === "all" ? true : i.city === filterCity))
                    .filter(i => i.id.toLowerCase().includes(searchQuery.toLowerCase()))
                    .map((item) => (
                      <TableRow key={item.id} hover sx={{ bgcolor: 'white' }}>
                        <TableCell align="right">
                          <Chip label={item.city === 'baghdad' ? 'بغداد' : 'أربيل'} size="small" variant="filled" color={item.city === 'baghdad' ? 'primary' : 'secondary'} sx={{ fontWeight: 'bold', fontSize: '10px' }} />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold" sx={{ color: '#555' }}>{item.category}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Box>
                            <Typography variant="caption" display="block" fontWeight="bold">{item.size || "بلا قياس"}</Typography>
                            <Typography variant="caption" color="textSecondary">{item.material} {item.fullFrameKind} {item.frameColor} {item.frameType}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Box display="flex" alignItems="center" justifyContent="center" sx={{ bgcolor: '#f0f4f8', borderRadius: '20px', p: '2px' }}>
                            <IconButton size="small" sx={{ color: '#d32f2f' }} onClick={() => handleQuickUpdate(item, "remove")}>➖</IconButton>
                            <Typography variant="body2" fontWeight="900" sx={{ mx: 1 }}>{item.quantity}</Typography>
                            <IconButton size="small" sx={{ color: '#2e7d32' }} onClick={() => handleQuickUpdate(item, "add")}>➕</IconButton>
                          </Box>
                        </TableCell>
                        {isAdmin && <TableCell align="right" sx={{ fontSize: '12px' }}>{item.price?.toLocaleString()} IQD</TableCell>}
                        {isAdmin && (
                          <TableCell align="right" sx={{ fontWeight: 'bold', color: '#1976d2', fontSize: '12px' }}>
                            {((item.quantity || 0) * (item.price || 0)).toLocaleString()} IQD
                          </TableCell>
                        )}
                        <TableCell align="right" sx={{ fontSize: '10px', color: '#888', whiteSpace: 'nowrap' }}>
                          {item.updatedAt?.toDate ? item.updatedAt.toDate().toLocaleString('en-US', { dateStyle: 'short' }) : "-"}
                        </TableCell>
                        {isAdmin && (
                          <TableCell align="center">
                            <IconButton size="small" onClick={() => alert(item.logs?.map(l => `[${l.date}] ${l.type}: ${l.quantity}\nReason: ${l.reason}`).join('\n\n') || "No records")}>
                              <Typography fontSize="16px">📜</Typography>
                            </IconButton>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default BaneenInventory;